import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FaqCategory, FaqQuestion } from "@/types/faq";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: FaqCategory[];
  /** Null = create. Non-null = edit, and the code becomes read-only. */
  question: FaqQuestion | null;
  defaultCategoryKey?: string;
  isSaving: boolean;
  onSubmit: (values: {
    code: string;
    categoryKey: string;
    question: { ar: string; en: string };
    answer: { ar: string; en: string };
    sortOrder: number;
  }) => void;
}

const EMPTY = { ar: "", en: "" };

/**
 * Add or edit one question, both languages side by side.
 *
 * 🔴 THE CODE IS READ-ONLY WHEN EDITING. It is the question's anchor on the public page
 * (/Open-Budget-Survey-questionnaire#PBS-1) and what «انسخ رابط السؤال» has already handed to people; the backend refuses to
 * change it, so offering an editable box here would only produce a save that silently ignores it.
 *
 * ⚠️ AN EMPTY ANSWER IS A VALID SAVE, not an incomplete form. It is what puts a question back into
 * «لم تُنشر الإجابة بعد» on the site, and it is the state all 215 seeded questions start in — so
 * nothing here may require an answer.
 */
export function FaqQuestionDialog({
  open,
  onOpenChange,
  categories,
  question,
  defaultCategoryKey,
  isSaving,
  onSubmit,
}: Props) {
  const isEdit = question !== null;
  const [code, setCode] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [questionText, setQuestionText] = useState(EMPTY);
  const [answerText, setAnswerText] = useState(EMPTY);
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCode(question?.code ?? "");
    setCategoryKey(question?.categoryKey ?? defaultCategoryKey ?? categories[0]?.key ?? "");
    setQuestionText(question?.question ?? EMPTY);
    setAnswerText(question?.answer ?? EMPTY);
    setSortOrder(question?.sortOrder ?? 0);
  }, [open, question, defaultCategoryKey, categories]);

  // At least one language must carry the question text — the same rule the backend enforces, so
  // the dialog refuses locally instead of round-tripping to a 400.
  const canSave =
    code.trim().length > 0 &&
    categoryKey.length > 0 &&
    (questionText.ar.trim().length > 0 || questionText.en.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "تعديل السؤال" : "إضافة سؤال"}</DialogTitle>
          <DialogDescription>
            اترك الإجابة فارغة لعرض «لم تُنشر الإجابة بعد» على الموقع. إذا كانت
            الإجابة رابطًا فقط، فستظهر كرابط قابل للنقر.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="faq-code">رمز السؤال</Label>
              <Input
                id="faq-code"
                value={code}
                dir="ltr"
                disabled={isEdit}
                placeholder="PBS-1"
                onChange={(e) => setCode(e.target.value)}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  الرمز هو رابط السؤال على الموقع، ولا يمكن تغييره بعد الإنشاء.
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="faq-category">القسم</Label>
              <Select value={categoryKey} onValueChange={setCategoryKey}>
                <SelectTrigger id="faq-category">
                  <SelectValue placeholder="اختر القسم" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.title.ar || c.title.en || c.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="faq-q-ar">نص السؤال (عربي)</Label>
            <Textarea
              id="faq-q-ar"
              value={questionText.ar}
              rows={2}
              onChange={(e) => setQuestionText({ ...questionText, ar: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="faq-q-en">نص السؤال (إنجليزي)</Label>
            <Textarea
              id="faq-q-en"
              value={questionText.en}
              rows={2}
              dir="ltr"
              onChange={(e) => setQuestionText({ ...questionText, en: e.target.value })}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="faq-a-ar">الإجابة (عربي)</Label>
            <Textarea
              id="faq-a-ar"
              value={answerText.ar}
              rows={3}
              onChange={(e) => setAnswerText({ ...answerText, ar: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="faq-a-en">الإجابة (إنجليزي)</Label>
            <Textarea
              id="faq-a-en"
              value={answerText.en}
              rows={3}
              dir="ltr"
              onChange={(e) => setAnswerText({ ...answerText, en: e.target.value })}
            />
          </div>

          <div className="grid gap-2 sm:max-w-[12rem]">
            <Label htmlFor="faq-order">الترتيب داخل القسم</Label>
            <Input
              id="faq-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            إلغاء
          </Button>
          <Button
            disabled={!canSave || isSaving}
            onClick={() =>
              onSubmit({
                code: code.trim(),
                categoryKey,
                question: questionText,
                answer: answerText,
                sortOrder,
              })
            }
          >
            {isSaving ? "جارٍ الحفظ…" : "حفظ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
